import useSWRImmutable from 'swr/immutable';

import { useSnackbar } from '~/lib/hooks/useSnackbar';
import { makeStore } from '~/lib/makeStore';
import { ReadOnlyOptions } from '~/lib/sdk';
import { swrKeyFor } from '~/lib/swr';
import { useGeographicalState } from '~/lib/trainerSettings';

import { GradeList } from '../types';

interface GradesContext {
  grades: GradeList;
  selectedGrade: string | null;
}

const [GradesProvider, useSetGradesState, useGradesStore] =
  makeStore<GradesContext>({
    name: 'Grades',
    initialState: {
      grades: [],
      selectedGrade: null,
    },
    localStorageKey: 'skillDiscovery.grades',
    scopeToTeacher: true,
  });

export { GradesProvider };

interface UseGrades extends GradesContext {
  setSelectedGrade: (grade: string | null) => void;
}

type UseSelectedGrade = [
  UseGrades['selectedGrade'],
  UseGrades['setSelectedGrade'],
];

export const useSelectedGrade = (): UseSelectedGrade => {
  const { selectedGrade } = useGradesStore();
  const setState = useSetGradesState();
  const setSelectedGrade = (grade: string | null): void =>
    setState(draft => {
      draft.selectedGrade = grade;
    });

  return [selectedGrade, setSelectedGrade];
};

export const useGrades = ({ readOnly }: ReadOnlyOptions = {}): UseGrades => {
  const [selectedGrade, setSelectedGrade] = useSelectedGrade();
  const { grades: storedGrades } = useGradesStore();
  const setState = useSetGradesState();
  const [state] = useGeographicalState();
  const { enqueueGenericError } = useSnackbar();
  const swrKey = swrKeyFor(['collections.list', state]);
  // TODO: Use CollectionsIndexResponse
  const { data: grades } = useSWRImmutable<GradeList>(swrKey, {
    onError: () => {
      // If we stored grades, let's not bother the user for now
      if (storedGrades.length) return;

      enqueueGenericError();
    },
    onSuccess: data =>
      setState(draft => {
        draft.grades = data;
      }),
    ...(readOnly ? { revalidateOnMount: false } : {}),
  });

  return {
    grades: grades ?? storedGrades,
    selectedGrade,
    setSelectedGrade,
  };
};
